import { useState, useEffect } from "react";
import {
  X,
  Copy,
  Check,
  ChevronUp,
  ChevronDown,
  ExternalLink,
  Sparkles,
} from "lucide-react";
import api from "../api/client";
import { ROLE_CLASSES } from "../constants/statusColors";

const SCORE_COLORS = {
  strong:
    "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300",
  moderate:
    "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300",
  weak: "bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400",
};

const SCORE_LABELS = {
  strong: "Strong match",
  moderate: "Moderate",
  weak: "Weak",
};

export default function SharePanel({
  isOpen,
  onClose,
  positionId,
  submissions,
  authConfig,
  existingShareId,
}) {
  // Map of application_id → { selected, score_tier }
  // Score tier is fetched lazily once the candidate is selected
  const [selected, setSelected] = useState({});
  const [order, setOrder] = useState([]); // array of application_ids in agency's chosen order
  const [clientLabel, setClientLabel] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [loading, setLoading] = useState(false);
  const [shareResult, setShareResult] = useState(null); // { share_token, items: [...] }
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState(null);

  // Detail mode (when reopening an existing share)
  const [shareDetail, setShareDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [pushStatusOpen, setPushStatusOpen] = useState(false);
  const [bulkStatus, setBulkStatus] = useState("Screening");
  const [bulkNote, setBulkNote] = useState("");
  const [bulkPushing, setBulkPushing] = useState(false);

  // Reset when opened
  useEffect(() => {
    if (isOpen) {
      setSelected({});
      setOrder([]);
      setClientLabel("");
      setClientEmail("");
      setExpiresAt("");
      setShareResult(null);
      setError(null);
      setShareDetail(null);
      setPushStatusOpen(false);
      setBulkNote("");

      // If detail mode, fetch the existing share
      if (existingShareId) {
        setDetailLoading(true);
        api
          .get(`/shares/${existingShareId}/detail`, authConfig)
          .then(({ data }) => {
            setShareDetail(data);
          })
          .catch((err) => {
            setError(err.response?.data?.detail || "Failed to load share");
          })
          .finally(() => {
            setDetailLoading(false);
          });
      }
    }
  }, [isOpen, existingShareId]);

  const toggleSelect = (applicationId) => {
    setSelected((prev) => {
      const wasSelected = !!prev[applicationId];
      const next = { ...prev };
      if (wasSelected) {
        delete next[applicationId];
      } else {
        next[applicationId] = true;
      }
      // Update order outside, after determining new state
      setOrder((o) => {
        if (wasSelected) {
          return o.filter((id) => id !== applicationId);
        }
        // Only add if not already present (defensive against double-invoke)
        return o.includes(applicationId) ? o : [...o, applicationId];
      });
      return next;
    });
  };

  const moveUp = (index) => {
    if (index === 0) return;
    setOrder((o) => {
      const next = [...o];
      [next[index - 1], next[index]] = [next[index], next[index - 1]];
      return next;
    });
  };

  const moveDown = (index) => {
    setOrder((o) => {
      if (index === o.length - 1) return o;
      const next = [...o];
      [next[index], next[index + 1]] = [next[index + 1], next[index]];
      return next;
    });
  };

  const handleBulkPush = async () => {
    if (!shareDetail) return;
    const selectedAppIds = shareDetail.items
      .filter((item) => item.client_selected)
      .map((item) => item.application_id);

    if (selectedAppIds.length === 0) return;

    setBulkPushing(true);
    setError(null);
    try {
      await Promise.all(
        selectedAppIds.map((appId) =>
          api.post(
            `/employer/applications/${appId}/status`,
            {
              status: bulkStatus,
              note: bulkNote.trim() || null,
              actions: [],
            },
            authConfig,
          ),
        ),
      );
      setPushStatusOpen(false);
      setBulkNote("");
      // Refresh detail to show updated state
      const { data } = await api.get(
        `/shares/${existingShareId}/detail`,
        authConfig,
      );
      setShareDetail(data);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to push status");
    } finally {
      setBulkPushing(false);
    }
  };

  const handleSubmit = async () => {
    setError(null);
    if (order.length === 0) {
      setError("Select at least one candidate");
      return;
    }

    setLoading(true);
    try {
      const items = order.map((appId, idx) => ({
        application_id: appId,
        sort_order: idx,
      }));
      const body = {
        items,
        client_label: clientLabel.trim() || null,
        client_email: clientEmail.trim() || null,
        expires_at: expiresAt ? new Date(expiresAt).toISOString() : null,
      };
      const { data } = await api.post(
        `/job-positions/${positionId}/shares`,
        body,
        authConfig,
      );
      setShareResult(data);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to create share");
    } finally {
      setLoading(false);
    }
  };

  const shareUrl = shareResult
    ? `${window.location.origin}/share/${shareResult.share_token}`
    : "";

  const copyShareUrl = () => {
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!isOpen) return null;

  // Find candidate info from submissions list
  const candidateInfo = (appId) => {
    const sub = submissions.find((s) => s.application_id === appId);
    if (!sub) return { name: appId, jobTitle: "" };
    return {
      name: sub.candidate_full_name || sub.candidate_initial || "Unknown",
      jobTitle: sub.job_title || "",
    };
  };

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
      <div className="relative w-full max-w-2xl h-full bg-white dark:bg-brand-dark-surface shadow-2xl overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white dark:bg-brand-dark-surface border-b border-gray-200 dark:border-white/10 px-6 py-4 flex items-center justify-between z-10">
          <div>
            <h2 className="text-lg font-semibold">Share with client</h2>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              {shareResult
                ? "Share created"
                : "Select and order candidates to share"}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6">
          {/* Detail mode (reopened existing share) */}
          {existingShareId ? (
            detailLoading ? (
              <p className="text-sm text-gray-500">Loading share...</p>
            ) : shareDetail ? (
              <div className="space-y-6">
                {/* Share metadata */}
                <div className="bg-gray-50 dark:bg-brand-dark-bg rounded-lg p-4 space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Client</span>
                    <span className="font-medium">
                      {shareDetail.client_label || "Untitled"}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Created</span>
                    <span>
                      {new Date(shareDetail.created_at).toLocaleDateString(
                        "en-GB",
                        {
                          day: "numeric",
                          month: "short",
                          year: "numeric",
                        },
                      )}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Views</span>
                    <span>{shareDetail.view_count}</span>
                  </div>
                  {shareDetail.first_viewed_at && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">First viewed</span>
                      <span>
                        {new Date(shareDetail.first_viewed_at).toLocaleString(
                          "en-GB",
                          {
                            day: "numeric",
                            month: "short",
                            hour: "2-digit",
                            minute: "2-digit",
                          },
                        )}
                      </span>
                    </div>
                  )}
                  {shareDetail.feedback_submitted_at && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Feedback received</span>
                      <span className="text-green-600 dark:text-green-400 font-medium">
                        {new Date(
                          shareDetail.feedback_submitted_at,
                        ).toLocaleString("en-GB", {
                          day: "numeric",
                          month: "short",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </span>
                    </div>
                  )}
                </div>

                {/* Share URL — for forwarding again */}
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-widest mb-2">
                    Share link
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={`${window.location.origin}/share/${shareDetail.share_token}`}
                      readOnly
                      className="flex-1 px-3 py-2 bg-gray-50 dark:bg-brand-dark-bg border border-gray-200 dark:border-white/10 rounded-md text-sm font-mono"
                    />
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(
                          `${window.location.origin}/share/${shareDetail.share_token}`,
                        );
                        setCopied(true);
                        setTimeout(() => setCopied(false), 2000);
                      }}
                      className="px-3 py-2 border border-brand-violet dark:border-brand-violet-on-dark text-brand-violet dark:text-brand-violet-on-dark rounded-md text-sm font-medium hover:bg-brand-violet-tint dark:hover:bg-brand-violet/10"
                    >
                      {copied ? (
                        <Check className="w-4 h-4" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </div>

                {/* Client comment */}
                {shareDetail.client_comment && (
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-widest mb-2">
                      Client comment
                    </label>
                    <div className="p-3 bg-gray-50 dark:bg-brand-dark-bg rounded-md text-sm whitespace-pre-wrap">
                      {shareDetail.client_comment}
                    </div>
                  </div>
                )}

                {/* Candidates with selection state */}
                <div>
                  <h3 className="font-medium mb-3">
                    Candidates ({shareDetail.items.length})
                  </h3>
                  <ol className="space-y-2">
                    {shareDetail.items.map((item, idx) => (
                      <li
                        key={item.id}
                        className={`flex items-start gap-3 p-3 rounded-md border ${
                          item.client_selected
                            ? "border-green-300 bg-green-50 dark:bg-green-900/10 dark:border-green-800"
                            : "border-gray-200 dark:border-white/10"
                        }`}
                      >
                        <span className="text-sm font-semibold text-gray-400 w-6">
                          {idx + 1}.
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">
                            {item.candidate_full_name ||
                              item.candidate_email ||
                              "Unknown"}
                          </p>
                          {item.job_title && (
                            <p className="text-xs text-gray-500 truncate">
                              {item.job_title}
                            </p>
                          )}
                        </div>
                        <span
                          className={`text-xs px-2 py-0.5 rounded-full font-medium ${SCORE_COLORS[item.score_tier]}`}
                        >
                          {SCORE_LABELS[item.score_tier]}
                        </span>
                        {item.client_selected && (
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${ROLE_CLASSES.active}`}>
                            Highlighted
                          </span>
                        )}
                      </li>
                    ))}
                  </ol>
                </div>

                {/* Bulk push status (only if there are highlighted candidates) */}
                {shareDetail.feedback_submitted_at && (
                  <>
                    {(() => {
                      const highlightedCount = shareDetail.items.filter(
                        (i) => i.client_selected,
                      ).length;
                      if (highlightedCount === 0) {
                        return (
                          <div className="p-4 bg-gray-50 dark:bg-brand-dark-bg rounded-md text-sm text-gray-500 italic">
                            Client did not highlight any candidates.
                          </div>
                        );
                      }
                      if (!pushStatusOpen) {
                        return (
                          <button
                            onClick={() => setPushStatusOpen(true)}
                            className="w-full py-2.5 bg-brand-violet text-white rounded-md text-sm font-medium hover:opacity-90"
                          >
                            Push status to {highlightedCount} highlighted
                            candidate{highlightedCount !== 1 && "s"}
                          </button>
                        );
                      }
                      return (
                        <div className="space-y-3 p-4 bg-gray-50 dark:bg-brand-dark-bg rounded-md">
                          <h4 className="font-medium text-sm">
                            Push status to highlighted candidates
                          </h4>
                          <div>
                            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-widest mb-1">
                              New status
                            </label>
                            <select
                              value={bulkStatus}
                              onChange={(e) => setBulkStatus(e.target.value)}
                              className="w-full px-3 py-2 bg-white dark:bg-brand-dark-surface border border-gray-200 dark:border-white/10 rounded-md text-sm"
                            >
                              <option value="Screening">Screening</option>
                              <option value="Interview">Interview</option>
                              <option value="Technical test">
                                Technical test
                              </option>
                              <option value="Final interview">
                                Final interview
                              </option>
                              <option value="Offer received">
                                Offer received
                              </option>
                              <option value="Rejected">Rejected</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-widest mb-1">
                              Note (optional)
                            </label>
                            <textarea
                              value={bulkNote}
                              onChange={(e) => setBulkNote(e.target.value)}
                              rows={2}
                              placeholder="Optional context for the candidate..."
                              className="w-full px-3 py-2 bg-white dark:bg-brand-dark-surface border border-gray-200 dark:border-white/10 rounded-md text-sm resize-none"
                            />
                          </div>
                          {error && (
                            <div className="text-sm text-red-600 dark:text-red-400">
                              {error}
                            </div>
                          )}
                          <div className="flex gap-2">
                            <button
                              onClick={() => setPushStatusOpen(false)}
                              disabled={bulkPushing}
                              className="flex-1 py-2 border border-gray-200 dark:border-white/10 rounded-md text-sm font-medium hover:bg-gray-50 dark:hover:bg-white/5 disabled:opacity-50"
                            >
                              Cancel
                            </button>
                            <button
                              onClick={handleBulkPush}
                              disabled={bulkPushing}
                              className="flex-1 py-2 bg-brand-violet text-white rounded-md text-sm font-medium hover:opacity-90 disabled:opacity-50"
                            >
                              {bulkPushing ? "Pushing..." : "Push to all"}
                            </button>
                          </div>
                        </div>
                      );
                    })()}
                  </>
                )}

                <button
                  onClick={onClose}
                  className="w-full py-2.5 border border-gray-200 dark:border-white/10 rounded-md text-sm font-medium hover:bg-gray-50 dark:hover:bg-white/5"
                >
                  Close
                </button>
              </div>
            ) : (
              <p className="text-sm text-red-600 dark:text-red-400">
                {error || "Share not found"}
              </p>
            )
          ) : shareResult ? (
            <div className="space-y-6">
              <div className="flex items-start gap-3 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                <Sparkles className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-green-900 dark:text-green-200 mb-1">
                    Share created successfully
                  </p>
                  <p className="text-sm text-green-700 dark:text-green-300">
                    Forward this link to your client. We'll show you when they
                    open it and which candidates they highlight.
                  </p>
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-widest mb-2">
                  Share link
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={shareUrl}
                    readOnly
                    className="flex-1 px-3 py-2 bg-gray-50 dark:bg-brand-dark-bg border border-gray-200 dark:border-white/10 rounded-md text-sm font-mono"
                  />
                  <button
                    onClick={copyShareUrl}
                    className="px-3 py-2 border border-brand-violet dark:border-brand-violet-on-dark text-brand-violet dark:text-brand-violet-on-dark rounded-md text-sm font-medium hover:bg-brand-violet-tint dark:hover:bg-brand-violet/10"
                  >
                    {copied ? (
                      <Check className="w-4 h-4" />
                    ) : (
                      <Copy className="w-4 h-4" />
                    )}
                  </button>
                  <a
                    href={shareUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-3 py-2 border border-gray-200 dark:border-white/10 rounded-md text-sm font-medium hover:bg-gray-50 dark:hover:bg-white/5"
                  >
                    <ExternalLink className="w-4 h-4" />
                  </a>
                </div>
              </div>

              <div>
                <h3 className="font-medium mb-3">Candidates included</h3>
                <ol className="space-y-2">
                  {shareResult.items.map((item, idx) => (
                    <li
                      key={item.id}
                      className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-brand-dark-bg rounded-md"
                    >
                      <span className="text-sm font-semibold text-gray-400 w-6">
                        {idx + 1}.
                      </span>
                      <div className="flex-1">
                        <p className="text-sm font-medium">
                          {item.candidate_full_name ||
                            item.candidate_email ||
                            "Unknown"}
                        </p>
                        {item.job_title && (
                          <p className="text-xs text-gray-500">
                            {item.job_title}
                          </p>
                        )}
                      </div>
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full font-medium ${SCORE_COLORS[item.score_tier]}`}
                      >
                        {SCORE_LABELS[item.score_tier]}
                      </span>
                    </li>
                  ))}
                </ol>
              </div>

              <button
                onClick={onClose}
                className="w-full py-2.5 border border-gray-200 dark:border-white/10 rounded-md text-sm font-medium hover:bg-gray-50 dark:hover:bg-white/5"
              >
                Done
              </button>
            </div>
          ) : (
            /* Create-share view */
            <div className="space-y-6">
              {/* Available candidates */}
              <div>
                <h3 className="font-medium mb-3">Available candidates</h3>
                {submissions.length === 0 ? (
                  <p className="text-sm text-gray-500">
                    No submissions yet for this position.
                  </p>
                ) : (
                  <div className="space-y-2 max-h-64 overflow-y-auto pr-2">
                    {submissions.map((sub) => {
                      const isSelected = !!selected[sub.application_id];
                      return (
                        <label
                          key={sub.application_id}
                          className={`flex items-start gap-3 p-3 rounded-md border cursor-pointer transition ${
                            isSelected
                              ? "border-brand-violet dark:border-brand-violet-on-dark bg-brand-violet-tint dark:bg-brand-violet/10"
                              : "border-gray-200 dark:border-white/10 hover:bg-gray-50 dark:hover:bg-white/5"
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleSelect(sub.application_id)}
                            className="mt-0.5"
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">
                              {sub.candidate_full_name ||
                                sub.candidate_initial ||
                                "Unknown"}
                            </p>
                            {sub.job_title && (
                              <p className="text-xs text-gray-500 truncate">
                                {sub.job_title}
                              </p>
                            )}
                            <p className="text-xs text-gray-400 mt-0.5">
                              Status: {sub.current_status}
                            </p>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Selected candidates with ordering */}
              {order.length > 0 && (
                <div>
                  <h3 className="font-medium mb-3">
                    Selected order ({order.length})
                  </h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                    Top candidate appears first to the client. Drag is coming
                    soon — for now, use the arrows.
                  </p>
                  <ol className="space-y-2">
                    {order.map((appId, idx) => {
                      const info = candidateInfo(appId);
                      return (
                        <li
                          key={appId}
                          className="flex items-center gap-2 p-3 bg-gray-50 dark:bg-brand-dark-bg rounded-md"
                        >
                          <span className="text-sm font-semibold text-gray-400 w-6">
                            {idx + 1}.
                          </span>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">
                              {info.name}
                            </p>
                            {info.jobTitle && (
                              <p className="text-xs text-gray-500 truncate">
                                {info.jobTitle}
                              </p>
                            )}
                          </div>
                          <button
                            onClick={() => moveUp(idx)}
                            disabled={idx === 0}
                            className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 disabled:opacity-30 disabled:cursor-not-allowed"
                          >
                            <ChevronUp className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => moveDown(idx)}
                            disabled={idx === order.length - 1}
                            className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 disabled:opacity-30 disabled:cursor-not-allowed"
                          >
                            <ChevronDown className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => toggleSelect(appId)}
                            className="p-1 text-gray-400 hover:text-red-500"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </li>
                      );
                    })}
                  </ol>
                </div>
              )}

              {/* Optional metadata */}
              <div className="space-y-3">
                <h3 className="font-medium">Optional details</h3>

                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-widest mb-1">
                    Client label
                  </label>
                  <input
                    type="text"
                    value={clientLabel}
                    onChange={(e) => setClientLabel(e.target.value)}
                    placeholder="e.g., Acme Corp"
                    className="w-full px-3 py-2 bg-white dark:bg-brand-dark-bg border border-gray-200 dark:border-white/10 rounded-md text-sm"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-widest mb-1">
                    Client email{" "}
                    <span className="text-gray-400 normal-case font-normal">
                      (optional)
                    </span>
                  </label>
                  <input
                    type="email"
                    value={clientEmail}
                    onChange={(e) => setClientEmail(e.target.value)}
                    placeholder="contact@acmecorp.com"
                    className="w-full px-3 py-2 bg-white dark:bg-brand-dark-bg border border-gray-200 dark:border-white/10 rounded-md text-sm"
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    For your records. We don't email anyone — you forward the
                    link yourself.
                  </p>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-widest mb-1">
                    Expires{" "}
                    <span className="text-gray-400 normal-case font-normal">
                      (optional)
                    </span>
                  </label>
                  <input
                    type="date"
                    value={expiresAt}
                    onChange={(e) => setExpiresAt(e.target.value)}
                    className="w-full px-3 py-2 bg-white dark:bg-brand-dark-bg border border-gray-200 dark:border-white/10 rounded-md text-sm"
                  />
                </div>
              </div>

              {error && (
                <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md text-sm text-red-700 dark:text-red-300">
                  {error}
                </div>
              )}

              <button
                onClick={handleSubmit}
                disabled={loading || order.length === 0}
                className="w-full py-2.5 bg-brand-violet text-white rounded-md text-sm font-medium hover:opacity-90 disabled:opacity-50"
              >
                {loading ? "Creating share..." : "Generate share link"}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
